import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { Bot, X, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const SUGGESTIONS = [
  "How do I link my WhatsApp?",
  "How do I set my prices?",
  "How do customers book me?",
  "How do I add a booking myself?",
];

export const SupportBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your TradieDesk helper 👋 Ask me anything about using the app — sharing your chatbot, setting prices, bookings, billing and more." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.post("/support/chat", { message: msg, history });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now — please try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="support-bot-toggle" onClick={() => setOpen(true)}
          className="fixed bottom-20 lg:bottom-5 right-5 z-50 flex items-center gap-2.5 rounded-full bg-primary hover:bg-orange-600 text-white shadow-lg shadow-black/40 pl-2 pr-4 py-2 transition-transform duration-200 hover:scale-105"
          style={{ marginBottom: "env(safe-area-inset-bottom)" }} aria-label="Open the TradieDesk help assistant">
          <span className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Bot className="h-5 w-5 text-white" /></span>
          <span className="text-sm font-semibold whitespace-nowrap">Need assistance?</span>
        </button>
      )}

      {open && (
        <div data-testid="support-bot-panel" className="fixed bottom-20 lg:bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm h-[540px] max-h-[80vh] bg-[#0f0f11] border border-white/15 rounded-lg shadow-2xl shadow-black/50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[#18181b] border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center"><Bot className="h-4 w-4 text-white" /></div>
              <div><p className="text-sm font-semibold leading-tight">TradieDesk Helper</p><p className="text-[11px] text-muted-foreground leading-tight">Here to help you use the app</p></div>
            </div>
            <button data-testid="support-bot-close" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-sm rounded-lg px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-white" : "bg-secondary text-foreground"}`}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-secondary rounded-lg px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div></div>}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="support-suggestion" onClick={() => send(s)} className="text-xs border border-white/10 rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">{s}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <input data-testid="support-bot-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…" className="flex-1 min-w-0 bg-[#18181b] border border-white/10 rounded-sm px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <Button data-testid="support-bot-send" onMouseDown={(e) => e.preventDefault()} onClick={() => send()} disabled={busy} className="rounded-sm bg-primary hover:bg-orange-600 text-white px-3 shrink-0"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
};
